/**
 * Confirmación profesional (design system, NO el modal nativo del navegador).
 *
 * Uso (promise-based):
 *   const confirm = useConfirm();
 *   const r = await confirm({ title, message, danger, confirmLabel });
 *   if (!r) return;                  // canceló
 *   // con input:
 *   const r = await confirm({ title, input: { label, multiline } });
 *   if (!r) return; const note = r.value;
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";

interface ConfirmInput {
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  required?: boolean;
  defaultValue?: string;
}

interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  input?: ConfirmInput;
}

interface ConfirmResult {
  value: string;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<ConfirmResult | null>;

const Ctx = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const fn = useContext(Ctx);
  if (!fn) throw new Error("useConfirm debe usarse dentro de <ConfirmProvider>");
  return fn;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [text, setText] = useState("");
  const resolver = useRef<((r: ConfirmResult | null) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o);
    setText(o.input?.defaultValue ?? "");
    return new Promise<ConfirmResult | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (result: ConfirmResult | null) => {
    resolver.current?.(result);
    resolver.current = null;
    setOpts(null);
    setText("");
  };

  const canConfirm = !opts?.input?.required || text.trim().length > 0;

  return (
    <Ctx.Provider value={confirm}>
      {children}
      <Modal open={opts !== null} onClose={() => close(null)} title={opts?.title ?? ""} maxWidth="md">
        {opts && (
          <div className="space-y-4">
            {opts.message && <div className="text-sm text-slate-700">{opts.message}</div>}
            {opts.input && (
              <label className="block text-sm">
                {opts.input.label && <span className="mb-1 block font-medium text-slate-700">{opts.input.label}</span>}
                {opts.input.multiline ? (
                  <textarea
                    autoFocus rows={3}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    placeholder={opts.input.placeholder} value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                ) : (
                  <input
                    autoFocus
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    placeholder={opts.input.placeholder} value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                )}
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => close(null)}>
                {opts.cancelLabel ?? "Cancelar"}
              </Button>
              <Button
                variant={opts.danger ? "danger" : "primary"}
                disabled={!canConfirm}
                onClick={() => close({ value: text })}
              >
                {opts.confirmLabel ?? "Confirmar"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Ctx.Provider>
  );
}
